import React from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Box, ButtonBase, Divider, IconButton, Toolbar, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { LoadingTypography } from 'lib/LoadingTypography';
import { isString } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ApplicationState } from 'store/store';

// TODO: if we are to move to this styled, they should be moved to the ui folder
import { PayTableInfo } from 'components/pay/header/PayTableInfo';
import { PayAvatar } from 'components/pay/header/PayAvatar';

const PREFIX = 'FlatToolbar';

const classes = {
    title: `${PREFIX}title`,
    emptyButton: `${PREFIX}emptyButton`,
    iconButton: `${PREFIX}iconButton`,
    buttonBase: `${PREFIX}buttonBase`
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    width: '100%',
    margin: '0 auto',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
    minHeight: '30px',
    alignItems: 'flex-start',
    [`& .${classes.title}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        fontWeight: 'bold',
        color: theme.palette.titleTextColour,
        height: '30px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        maxWidth: '80vw'
    },

    [`& .${classes.emptyButton}`]: {
        width: '24px',
        height: 1
    },

    [`& .${classes.iconButton}`]: {
        margin: theme.spacing(-1.5),
        marginTop: theme.spacing(-1.25)
    },

    [`& .${classes.buttonBase}`]: {
        display: 'flex',
        flexDirection: 'column'
    }
}));

interface FlatToolbarProps {
    title?: string | null;
    description?: string | null;
    table?: string | null;
    checkId?: string | null;
    onChangeClick?: () => void;
    onBack?: () => void;
    loading?: boolean;
    showMenuIcon?: boolean;
    onMenuIconClick?: () => void;
    disableEdit?: boolean;
    disableBack?: boolean;
    hideUserAvatar?: boolean;
}

export const FlatToolbar: React.FC<FlatToolbarProps> = ({
    title,
    description,
    table,
    checkId,
    onBack,
    onChangeClick,
    showMenuIcon,
    onMenuIconClick,
    loading,
    disableEdit,
    disableBack,
    hideUserAvatar
}) => {
    const { navigate } = useLocalHistory();
    const { currentLocation, isCurrentLocationLoading } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const handleLeftClick = React.useCallback(() => {
        if (typeof onBack === 'function') {
            onBack();
        } else {
            navigate(-1);
        }
    }, [onBack, navigate]);
    const handleChangeClick = React.useCallback(() => {
        if (!disableEdit && onChangeClick) {
            onChangeClick();
        }
    }, [disableEdit, onChangeClick]);
    const isLoading = loading || (!title && isCurrentLocationLoading);
    const showTableInfo = (isString(table) || isString(checkId)) && !isString(description);
    return (
        <>
            <StyledToolbar disableGutters variant="dense">
                {!disableBack && (
                    <IconButton onClick={handleLeftClick} className={classes.iconButton} size="large">
                        <ArrowBackIcon color="inherit" />
                    </IconButton>
                )}
                {disableBack &&
                    (showMenuIcon && !!onMenuIconClick ? (
                        <IconButton onClick={onMenuIconClick} className={classes.iconButton} size="large">
                            <MenuIcon color="inherit" />
                        </IconButton>
                    ) : (
                        <div className={classes.emptyButton} />
                    ))}
                <ButtonBase
                    className={classes.buttonBase}
                    disableRipple={disableEdit}
                    onClick={handleChangeClick}
                >
                    <LoadingTypography
                        loading={isLoading}
                        align="center"
                        variant="h6"
                        className={classes.title}
                        width={200}
                    >
                        {title ?? currentLocation?.title}
                    </LoadingTypography>
                    {showTableInfo && (
                        <PayTableInfo
                            isLoading={isLoading}
                            checkId={checkId}
                            table={table}
                            disableEdit={disableEdit}
                        />
                    )}
                    {isString(description) && (
                        <Typography
                            color="secondary"
                            sx={{
                                opacity: 0.4,
                                width: '100%',
                                maxWidth: '75vw',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                </ButtonBase>
                {!hideUserAvatar ? (
                    <PayAvatar className={classes.iconButton} />
                ) : (
                    <Box sx={{ height: '24px', width: '24px' }} />
                )}
            </StyledToolbar>
            <Divider />
        </>
    );
};
